import React, { useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { apply } from '../../features/ContainerQuery'
import useResizeObserver from '../../features/useResizeObserver'

/**
 * Bronson ProductTable component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-product-table.html
 *
 * @param {ProductTableSection[]} [children] - A collection of {@link ProductTableSection} items.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {number} [headingLevel=3] - The heading level for the title element, e.g. `3` equals `<h3>`.
 * @param {React.ReactNode} [info] - Additional table info content.
 * @param {DescriptionList} [summary] - An optional summary {@link DescriptionList}.
 * @param {string} [title] - The title content.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The ProductTable element.
 * @constructor
 */
export function ProductTable({ children, className, headingLevel = 3, info, summary, title, testId, ...otherProps }) {
  const ref = useRef(null)
  const applyQC = useCallback(apply, [apply])
  /**
   * Apply `onResizeHandler` handler.
   */
  useResizeObserver({
    ref,
    onResizeHandler: applyQC,
  })

  const classNameList = classNames('c-product-table', className).trim()
  const CustomProductTableHeadingLevel = `h${headingLevel}`

  const productTableHeading = (
    <CustomProductTableHeadingLevel className="c-product-table__title">{title}</CustomProductTableHeadingLevel>
  )

  const productTableSummary = (
    <div className="c-product-table__section  c-product-table__section--summary">{summary}</div>
  )

  const productTableInfo = <div className="c-product-table__information">{info}</div>

  return (
    <div ref={ref} className={classNameList} data-testid={testId} {...otherProps}>
      {title && productTableHeading}
      {children}
      {summary && productTableSummary}
      {info && productTableInfo}
    </div>
  )
}

ProductTable.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.node]), // Bronson template: 'sections'.
  className: PropTypes.string, // Additional CSS classes on the component element. Bronson template: 'modifiers'.
  headingLevel: PropTypes.number, // Bronson template: 'title-heading-level'.
  info: PropTypes.oneOfType([PropTypes.element, PropTypes.node]), // Bronson template: 'table-info'.
  summary: PropTypes.oneOfType([PropTypes.element, PropTypes.node]), // Bronson template: 'summary'.
  title: PropTypes.string, // Bronson template: 'title'.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}

/**
 * Bronson ProductTableSection (nested).
 * @internal
 *
 * @param {DescriptionList[]} [children] - A collection of {@link DescriptionList} items.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {number} [innerHeadingLevel=4] - The heading level for the section title element, e.g. `4` equals `<h4>`.
 * @param {string} [innerTitle] - The section title content.
 * @returns {JSX.Element} - The ProductTableSection element.
 * @constructor
 */
export function ProductTableSection({ children, className, innerHeadingLevel = 4, innerTitle }) {
  const classNameList = classNames('c-product-table__section', className).trim()
  const CustomProductTableSectionHeadingLevel = `h${innerHeadingLevel}`

  const productTableSectionHeading = (
    <CustomProductTableSectionHeadingLevel className="c-product-table__inner-title">
      {innerTitle}
    </CustomProductTableSectionHeadingLevel>
  )

  return (
    <div className={classNameList}>
      {innerTitle && productTableSectionHeading}
      {children}
    </div>
  )
}

ProductTableSection.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.node]),
  className: PropTypes.string, // Additional CSS classes on the component element.
  innerHeadingLevel: PropTypes.number, // Bronson template: 'inner-title-heading-level'.
  innerTitle: PropTypes.string, // Bronson template: 'inner-title'.
}

ProductTableSection.displayName = 'ProductTable.Section'
ProductTable.Section = ProductTableSection

/**
 * Bronson ProductTableComment (nested).
 * @internal
 *
 * @param {string} [children] - The comment content.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {('term'|'detail')} type - Either 'term' or 'detail', depending upon where the comment is inserted.
 * @returns {JSX.Element} - The ProductTableComment element.
 * @constructor
 */
export function ProductTableComment({ children, className, type }) {
  const classNameList = classNames(
    {
      // Convenience props from Bronson.
      'c-product-table__item-term-comment': type === 'term',
      'c-product-table__item-detail-comment': type === 'detail',
    },
    className
  ).trim()

  return <span className={classNameList}>{children}</span>
}

ProductTableComment.propTypes = {
  children: PropTypes.string,
  className: PropTypes.string, // Additional CSS classes on the component element.
  /* Convenience props. */
  type: PropTypes.oneOf(['term', 'detail']).isRequired, // Convenience prop for `.c-product-table__item-term-comment` and `.c-product-table__item-detail-comment`.
}

ProductTableComment.displayName = 'ProductTable.Comment'
ProductTable.Comment = ProductTableComment
